import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import PageForm from '../../templates/PageForm';
import SelectFilter from '../../organisms/SelectFilter';
import CopyPasteTable from '../../organisms/CopyPasteTable';

const ISSUANCE_URL = `${process.env.REACT_APP_FUNDING_API_URL}/issuances`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

const BROKERS = [
    { value: '37', label: 'NU - 37', name: 'NU' },
    { value: '36', label: 'TORO - 36', name: 'TORO' },
    { value: '38', label: 'XP - 38', name: 'XP' },
    { value: '60', label: 'ÁGORA - 60', name: 'ÁGORA' },
    { value: '51', label: 'ITAU - 51', name: 'ITAU' },
    { value: '43', label: 'BTG - 43', name: 'BTG' },
    { value: '108', label: 'MIRAE - 108', name: 'MIRAE' },
    { value: '94', label: 'NIKOS - 94', name: 'NIKOS' },
    { value: '116', label: 'INTER - 116', name: 'INTER' },
    { value: '86', label: 'GENIAL INST - 86', name: 'GENIAL' },
    { value: '35', label: 'DAYCOVAL - 35', name: 'DAYCOVAL' },
];

const BROKER_FIELD_MAPPING: Record<string, Record<string, string>> = {
    DAYCOVAL: {
        'TIPO IF': 'product',
        'VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    XP: {
        'ATIVO': 'product',
        'DT. VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA PRÉ-FIXADA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
    },
    TORO: {
        'ATIVO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    GENIAL: {
        'TÍTULO': 'product',
        'VENCTO': 'due_date',
        'ÍNDICE': 'index',
        'TAXA DE EMISSÃO': 'tax',
        'QTDE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    NIKOS: {
        'PAPEL': 'product',
        'VENCIMENTO': 'due_date',
        'INDICE': 'index',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    ÁGORA: {
        'ATIVO': 'product',
        'VCTO': 'due_date',
        'INDICE': 'index',
        'TX EMISSÃO': 'tax',
        'QTDE': 'quantity',
        'PU DESÁGIO': 'pu_degassed',
    },
    INTER: {
        'ATIVO': 'product',
        'PRAZO / VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA CLIENTE (EMISSÃO)': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    ITAU: {
        'PRODUTO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    BTG: {
        'PRODUTO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    NU: {
        'TITULO': 'product',
        'VCTO': 'due_date',
        'INDICE': 'index',
        'TAXA CLIENTE': 'tax',
        'QTDE TOTAL': 'quantity',
        'PU': 'pu_degassed',
    },
    MIRAE: {
        'ATIVO': 'product',
        'VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
    },
};

const BROKER_HEADERS: Record<string, { label: string; field: string; required: boolean; format: string }[]> = {
    DAYCOVAL: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
    XP: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
    TORO: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
    GENIAL: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
    NIKOS: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
    ÁGORA: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
    INTER: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
    ITAU: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
    BTG: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
    NU: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
    MIRAE: [
        { label: 'Produto', field: 'product', required: true, format: 'text' },
        { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
        { label: 'Indexador', field: 'index', required: true, format: 'text' },
        { label: 'Taxa', field: 'tax', required: true, format: 'text' },
        { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
        { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    ],
};

interface Form {
    brokerId: string;
    issuances?: any[];
}

const IssuanceFormPage: React.FC = () => {
    const navigate = useNavigate();
    const form = useSignal<Form>({ brokerId: '', issuances: [] });
    const isSending = useSignal(false);
    const buttonDisabled = useSignal(true);
    const hasErrors = useSignal(false);
    const selectedBrokerName = useSignal('');
    const currentHeaders = useSignal<{ label: string; field: string; required: boolean; format: string }[]>([]);
    const currentMapping = useSignal<Record<string, string>>({});

    useEffect(() => {
        const handleJson = (event: CustomEvent) => {
            const data = event.detail as any[];
            form.value = { ...form.value, issuances: data };
            buttonDisabled.value = hasErrors.value || data.length === 0 || !form.value.brokerId;
        };
        window.addEventListener('CopyPasteTable:jsonGenerated', handleJson as EventListener);
        return () => window.removeEventListener('CopyPasteTable:jsonGenerated', handleJson as EventListener);
    }, []);

    useEffect(() => {
        const handleErrors = (event: CustomEvent) => {
            const errors = event.detail as unknown[];
            hasErrors.value = errors.length > 0;
            buttonDisabled.value = hasErrors.value || (form.value.issuances?.length || 0) === 0 || !form.value.brokerId;
        };
        window.addEventListener('CopyPasteTable:errors', handleErrors as EventListener);
        return () => window.removeEventListener('CopyPasteTable:errors', handleErrors as EventListener);
    }, [form.value.issuances?.length]);

    const clearData = () => {
        window.dispatchEvent(new Event('CopyPasteTable:clearData'));
    };

    const submitForm = async () => {
        if (buttonDisabled.value) {
            toast.error('Por favor, corrija os erros antes de enviar o formulário.');
            return;
        }

        isSending.value = true;
        try {
            const response = await fetch(ISSUANCE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY || ''
                },
                body: JSON.stringify({
                    brokerId: form.value.brokerId,
                    issuances: form.value.issuances,
                }),
            });
            if (response.ok) {
                toast.info('Emissões lançadas com sucesso.');
                navigate('/funding/issuance/list');
            } else {
                const { message } = await response.json();
                toast.error(message || 'Erro ao enviar formulário.');
            }
        } catch (err) {
            toast.error('Erro de rede ao enviar formulário.');
        }
        isSending.value = false;
    };

    const cancelForm = () => navigate(-1);

    return (
        <PageForm
            pageTitle='Lançar Emissões'
            apiVersion='Funding API - v1'
            isLoading={false}
            breadcrumb={[{ label: 'Tesouraria', url: '/funding/issuance/list' }, { label: 'Lançar Emissões', url: '#' }]}
            controlSlot={(
                <div className='flex space-x-4 justify-end m-2'>
                    {form.value.issuances && form.value.issuances.length > 0 && (
                        <button
                            className='bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-800'
                            onClick={clearData}
                            disabled={isSending.value}
                        >
                            Limpar Dados
                        </button>
                    )}
                    <button
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
                        onClick={cancelForm}
                        disabled={isSending.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className='bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700'
                        onClick={submitForm}
                        disabled={isSending.value || buttonDisabled.value}
                    >
                        Salvar
                    </button>
                </div>
            )}
        >
            <div className='container mx-auto p-4 space-y-4'>
                <SelectFilter
                    id='slc-broker'
                    label='Corretora:'
                    value={form.value.brokerId}
                    onChange={(value) => {
                        const broker = BROKERS.find(b => b.value === value);
                        if (broker) {
                            selectedBrokerName.value = broker.name;
                            form.value = { ...form.value, brokerId: value };
                            currentHeaders.value = BROKER_HEADERS[broker.name] || [];
                            currentMapping.value = BROKER_FIELD_MAPPING[broker.name] || {};
                            clearData();
                        }
                    }}
                    defaultVoidOption
                    defaultLabel='Selecione a corretora...'
                    options={BROKERS}
                />

                {form.value.brokerId && currentHeaders.value.length > 0 && (
                    <CopyPasteTable 
                        fixedHeaders={currentHeaders.value} 
                        allowDuplicates={true}
                        columnMapping={currentMapping.value}
                    />
                )}
            </div>
        </PageForm>
    );
};

export default IssuanceFormPage;
